import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Stream } from '../types/stream';
import { StreamService } from '../services/stream_service';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder } from '@angular/forms';


@Component({
    selector: 'stream-stats-modal',
    templateUrl: './stream-stats-modal.component.html',
    styleUrls: ['./stream-stats-modal.component.scss'],
    styles: [`
    .dp-hidden {
      width: 0;
      margin: 0;
      border: none;
      padding: 0;
    }
    .custom-day {
      text-align: center;
      padding: 0.185rem 0.25rem;
      display: inline-block;
      height: 2rem;
      width: 2rem;
    }
    .custom-day.focused {
      background-color: #e6e6e6;
    }
    .custom-day.range, .custom-day:hover {
      background-color: rgb(2, 117, 216);
      color: white;
    }
    .custom-day.faded {
      background-color: rgba(2, 117, 216, 0.5);
    }
  `]
})
export class StreamStatsModalComponent implements OnInit {
    @ViewChild('streamStatsModal', {static: false}) public streamStatsModal: ElementRef;
    @ViewChild('ngbDatepicker', {static: false}) public ngbDatepicker: ElementRef;
    @Input() public stream: Stream;
    public options: any;
    public chartParams: {
        paid: string;
        interval: string;
        from: {
            value: NgbDate | null;
            label: NgbDateStruct | null | string;
        };
        to: {
            value: NgbDate | null;
            label: NgbDateStruct | null | string;
        };
    } = {
        paid: 'paid',
        interval: 'month',
        from: {
            value: null,
            label: null
        },
        to: {
            value: null,
            label: null
        }
    };

    public paramsData = {
        paid: [
            {
                label: 'Paid',
                value: 'paid',
                selected: false
            },
            {
                label: 'Free',
                value: 'free',
                selected: false
            }
        ],
        interval: [
            {
                label: 'Day',
                value: 'day',
                selected: false
            },
            {
                label: 'Week',
                value: 'week',
                selected: false
            },
            {
                label: 'Month',
                value: 'month',
                selected: true
            }
        ]
    };

    public hoveredDate: NgbDate | null = null;
    public chartLoaded = false;
    private modalRef: any;
    private echartData: {
        xLabels: string[];
        data: number | string[];
    } = {
        xLabels: [],
        data: [],
    };

    constructor(
        public formatter: NgbDateParserFormatter,
        private streamService: StreamService,
        private fb: UntypedFormBuilder,
        private calendar: NgbCalendar,
        private modalService: NgbModal) {
            this.chartParams.from.value = calendar.getToday();
            this.chartParams.to.value = calendar.getNext(calendar.getToday(), 'd', 10);
        }

    public ngOnInit(): void {
        this.getStreamStats();
    }

    public showModal(): void {
        this.modalRef = this.modalService.open(this.streamStatsModal, { windowClass: 'stream-stats-modal', size: 'custom-size' });

        this.modalRef.result.then(result => {
            console.log(`Closed with:`, result);
        }, reason => {
            console.log(`Dismissed`, reason);
        })
        .catch(e => {
            console.log('error while closing modal: ', e);
        });
    }

    public closeModal(): void {
        this.modalRef.close();
    }

    public selectChartParam(type: string, ev: any, dp?: any): void {
        switch(type) {
            case 'paid':
                this.selectDropdownOption(type, ev);
                break;
            case 'interval':
                this.selectDropdownOption(type, ev);
                break;
            case 'range':
                const fromDate = this.chartParams.from.value;
                const toDate = this.chartParams.to.value;

                if (!fromDate && !toDate) {
                    this.chartParams.from.value = ev;
                  } else if (fromDate && !toDate && ev && ev.after(fromDate)) {
                    this.chartParams.to.value = ev;
                    dp.close();
                  } else {
                    this.chartParams.to.value = null;
                    this.chartParams.from.value = ev;
                  }
                break;
        }

        if (this.chartParams.from.value && this.chartParams.to.value) {
            this.getStreamStats();
        }
    }

    public isHovered(date: NgbDate) {
        const fromDate = this.chartParams.from.value;
        const toDate = this.chartParams.to.value;

        return fromDate && !toDate && this.hoveredDate && date.after(fromDate) &&
            date.before(this.hoveredDate);
    }

    public isInside(date: NgbDate) {
        const fromDate = this.chartParams.from.value;
        const toDate = this.chartParams.to.value;

        return toDate && date.after(fromDate) && date.before(toDate);
    }

    public isRange(date: NgbDate) {
        const fromDate = this.chartParams.from.value;
        const toDate = this.chartParams.to.value;

        return date.equals(fromDate) || (toDate && date.equals(toDate)) || this.isInside(date) ||
            this.isHovered(date);
    }

    public validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
        const parsed = this.formatter.parse(input);
        return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
    }

    public chartEvent(type: string): void {
        if (type === 'finished' || type === 'rendered') {
            this.chartLoaded = true;
        }
    }

    private getStreamStats(): void {
        const fromDateTimestamp = new Date(
            this.chartParams.from.value.year,
            this.chartParams.from.value.month - 1,
            this.chartParams.from.value.day).getTime();

        const toDateTimestamp = new Date(
            this.chartParams.to.value.year,
            this.chartParams.to.value.month - 1,
            this.chartParams.to.value.day).getTime();

        this.streamService.getStreamViewStats(
            this.stream.id,
            this.chartParams.paid,
            this.chartParams.interval,
            fromDateTimestamp,
            toDateTimestamp).then(stats => {
                const chartData = stats.raw.body.aggregations.view_stats.buckets || [];

                const xAxis = [];
                const data = [];

                for (const d of chartData) {
                    xAxis.push(new Date(d.key).toLocaleDateString());
                    data.push(d.doc_count);
                }

                this.echartData = {
                    xLabels: xAxis,
                    data
                };

                this.setEchartsOptions();
        });
    }

    private setEchartsOptions(): void {
        const xAxisData = this.echartData.xLabels;
        const data = this.echartData.data;

        this.options = {
            // legend: {
            //     data: ['bar'],
            //     align: 'left',
            // },
            tooltip: {},
            xAxis: {
                data: xAxisData,
                silent: false,
                splitLine: {
                    show: false,
                },
            },
            yAxis: {},
            series: [
                {
                    // name: 'bar',
                    type: 'bar',
                    data,
                    animationDelay: (idx) => idx * 10,
                }
            ],
            animationEasing: 'elasticOut',
            animationDelayUpdate: (idx) => idx * 5,
        };
    }

    private selectDropdownOption(type: string, val: any): void {
        this.chartParams[type] = val.value;
        for (const o of this.paramsData[type]) {
            o.selected = o.value === val.value;
        }
    }
}
